import moment from 'moment';
import { useMemo } from 'react';
import { NON_PLAYER_GENDER_WORK_OPTIONS } from '../../../../utils/constants';
import RadioGroup from '../../Custom/RadioGroup';
import { CONTRACT_STATUS_OPTIONS } from '../config/formConfig';
import FormField from './FormField';

const ClubSection = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    userInfo,
    teams,
    selectTeam,
    selectClub,
    searchValue,
    handleTeamChange,
    setSelectClub,
    setTeams,
    setSelectTeam,
    setNotFoundClubError,
    setSearchValue,
    getTeamsBasedOnSelectedClub,
    SelectWithPicture
}) => {
    const isGenderSpecific = values.gender && values.gender !== 'BOTH';

    const teamsData = useMemo(() => {
        let data = teams;

        if (!data?.length) {
            return [];
        }

        if (isGenderSpecific) {
            data = data.filter(team => team.clubGender === values.gender);
        }

        return data;
    }, [teams, values.gender, isGenderSpecific]);

    // Handle club selection and update Formik fields
    const handleClubSelect = club => {
        setSelectClub(club);
        if (setFieldValue) {
            setFieldValue('clubId', club?.id || '');
            setFieldValue('teamId', '');
        }
    };

    // Handle team selection and update Formik fields
    const handleTeamSelect = team => {
        setSelectTeam(team);
        if (setFieldValue) {
            setFieldValue('teamId', team?.id || '');
        }
    };

    // Enhanced team change handler
    const enhancedHandleTeamChange = event => {
        const selectedTeam =
            teams.find(item => item?.id === event.target.value) || {};

        handleTeamSelect(selectedTeam);
        handleTeamChange(event);
    };

    const handleContractStatusChange = event => {
        handleChange(event);

        if (setFieldValue) {
            setFieldValue('clubId', '');
            setFieldValue('teamId', '');
            setFieldValue('contractExpiry', '');
            setSelectClub({});
            setSelectTeam({});
            setSearchValue('');
        }
    };

    const handleChangeGender = event => {
        if (values.gender !== event.target.value) {
            setFieldValue('clubId', '');
            setFieldValue('teamId', '');
            setSelectClub({});
            setSelectTeam({});
            setSearchValue('');
            setFieldValue('gender', event.target.value);
        }
    };

    return (
        <section className='club-section'>
            {userInfo.userType !== 'NON_PLAYER' && (
                <h3 className='text-[14px] font-semibold my-5'>Club</h3>
            )}

            {userInfo.userType !== 'NON_PLAYER' && (
                <div
                    className='relative w-full mb-7 group'
                    data-field='contractStatus'
                    id='field-contractStatus'
                >
                    <label
                        className='text-sm text-gray-500'
                        htmlFor='contractStatus'
                    >
                        Contract Status
                    </label>
                    <div className='flex items-center w-full justify-between mt-3'>
                        <RadioGroup
                            name='contractStatus'
                            className='justify-between w-full'
                            value={
                                values.contractStatus || values.contractStatus
                            }
                            onChange={handleContractStatusChange}
                            onBlur={handleBlur}
                            options={CONTRACT_STATUS_OPTIONS}
                        />
                    </div>
                    {errors.contractStatus && touched.contractStatus && (
                        <p className='absolute mt-1 text-red-600 text-xs'>
                            {errors.contractStatus}
                        </p>
                    )}
                </div>
            )}

            {userInfo.userType === 'NON_PLAYER' && (
                <FormField
                    type='radio'
                    name='gender'
                    label='I work in…'
                    addBottomBorder={false}
                >
                    <RadioGroup
                        name='gender'
                        className='justify-between w-full'
                        value={values.gender}
                        onChange={handleChangeGender}
                        onBlur={handleBlur}
                        options={NON_PLAYER_GENDER_WORK_OPTIONS}
                    />
                </FormField>
            )}

            {/* Football Club section - only show for non-players or when contract status is CONTRACT or NON_CONTRACT */}
            {(userInfo.userType === 'NON_PLAYER' ||
                (userInfo.userType !== 'NON_PLAYER' &&
                    values.contractStatus &&
                    values.contractStatus !== 'UNSIGNED')) && (
                <FormField
                    name='clubId'
                    addBottomBorder={false}
                    label={
                        userInfo.userType !== 'NON_PLAYER'
                            ? 'Football Club'
                            : 'Club/Organisation'
                    }
                    error={errors.clubId}
                    touched={touched.clubId}
                    required={
                        userInfo.userType !== 'NON_PLAYER' &&
                        (values.contractStatus === 'CONTRACT' ||
                            values.contractStatus === 'NON_CONTRACT')
                    }
                >
                    <SelectWithPicture
                        setSelectClub={handleClubSelect}
                        selectClub={selectClub}
                        setTeams={setTeams}
                        borderColor={errors.clubId ? 'border-red-600' : ''}
                        className='absolute z-10'
                        setSelectTeam={handleTeamSelect}
                        setNotFoundClubError={setNotFoundClubError}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        getTeamsBasedOnSelectedClub={
                            getTeamsBasedOnSelectedClub
                        }
                        types={
                            userInfo?.userType !== 'NON_PLAYER'
                                ? ['DOMESTIC']
                                : undefined
                        }
                        gender={values.gender}
                    />
                </FormField>
            )}

            {/* Teams section - only show for non-players or when contract status is CONTRACT or NON_CONTRACT */}
            {((userInfo.userType === 'NON_PLAYER' &&
                selectClub?.typeOfOrg !== 'COMPANY') ||
                (userInfo.userType !== 'NON_PLAYER' &&
                    values.contractStatus &&
                    values.contractStatus !== 'UNSIGNED')) && (
                <FormField
                    name='teamId'
                    label='Team'
                    error={errors.teamId}
                    touched={touched.teamId}
                    required={
                        (!!selectClub?.id || !!values.clubId) &&
                        selectClub?.typeOfOrg !== 'COMPANY'
                    }
                >
                    <select
                        onChange={enhancedHandleTeamChange}
                        className='appearance-none bg-transparent w-full text-gray-700 py-1 pl-2 leading-tight focus:outline-none'
                        value={selectTeam?.id || values.teamId || ''}
                    >
                        <option value=''>Select team</option>
                        {teamsData.map(team => (
                            <option key={team?.id} value={team?.id}>
                                {isGenderSpecific
                                    ? team?.teamName
                                    : team?.teamNameWithGender}
                            </option>
                        ))}
                    </select>
                </FormField>
            )}

            {/* Contract Expiry section - only show for players with CONTRACT status */}
            {userInfo.userType !== 'NON_PLAYER' &&
                values.contractStatus === 'CONTRACT' && (
                    <FormField
                        name='contractExpiry'
                        label='Contract Expiry Date'
                        type='month'
                        value={
                            moment(values?.contractExpiry).isValid()
                                ? values.contractExpiry
                                : ''
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Select Contract Expiry'
                        error={errors.contractExpiry}
                        touched={touched.contractExpiry}
                        required={
                            userInfo.userType !== 'NON_PLAYER' &&
                            values.contractStatus === 'CONTRACT'
                        }
                    />
                )}
        </section>
    );
};

export default ClubSection;
